import { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { AdminSideExport } from "./Admin";

export default function AdminStoreImport() {
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [batch, setBatch] = useState(null);
  const [mode, setMode] = useState("both");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const pollRef = useRef();

  const loadHistory = () => api("/admin/store/import/history").then(setHistory).catch(() => {});
  useEffect(() => { loadHistory(); return () => clearInterval(pollRef.current); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setMsg(""); setPreview(null); setBatch(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const token = localStorage.getItem("mastofa_token");
      const r = await fetch(`${API}/admin/store/import/preview`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const js = await r.json();
      if (!r.ok) throw new Error(js.detail || "Upload gagal");
      setPreview(js);
      setMsg(`Preview siap: ${js.stats.total} baris terbaca.`);
    } catch (err) { setMsg(err.message); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true); setMsg("");
    try {
      await api("/admin/store/import/commit", { method: "POST", data: { batch_id: preview.batch_id, mode } });
      setMsg("Import berjalan di background...");
      pollRef.current = setInterval(async () => {
        const b = await api(`/admin/store/import/batch/${preview.batch_id}`);
        setBatch(b);
        if (b.status === "COMPLETED") {
          clearInterval(pollRef.current);
          setMsg("Import selesai.");
          setPreview(null);
          loadHistory();
        }
      }, 1500);
    } catch (err) { setMsg(err.message || "Commit gagal"); }
    setBusy(false);
  };

  const downloadTemplate = async () => {
    try {
      const csv = await api("/admin/store/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(csv);
      const a = document.createElement("a");
      a.href = url; a.download = "mass_product_import_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { setMsg("Gagal download template"); }
  };

  const st = preview?.stats;
  return (
    <div className="dashboard">
      <AdminSideExport active="store-import" />
      <main className="dash-main">
        <header className="dash-header">
          <span className="section-kicker">ADMIN · TOKO</span>
          <h1>Upload Produk Massal</h1>
          <p>Upload .xlsx/.csv → preview → konfirmasi import. SKU = identifier unik (SKU sama = UPDATE, SKU baru = CREATE).</p>
        </header>
        {msg && <div className="notice" data-testid="import-msg">{msg}</div>}
        <div className="admin-block" style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={upload} disabled={busy} data-testid="import-file-input" />
            <button type="button" className="button button-small button-ghost" onClick={downloadTemplate} data-testid="import-template-btn">Download Template</button>
          </div>
        </div>

        {preview && (
          <div className="admin-block" style={{ marginBottom: 16 }} data-testid="import-preview">
            <h3>Preview: {st.total} baris</h3>
            <p data-testid="import-stats">
              CREATE: <b>{st.create}</b> · UPDATE: <b>{st.update}</b> · INVALID: <b>{st.invalid}</b> · DUPLICATE: <b>{st.duplicate}</b>
            </p>
            <p style={{ fontSize: 12, color: "#94a3b8" }}>Mapping: {Object.entries(preview.mapping).map(([k, v]) => `${v}→${k}`).join(" · ")}</p>
            <div style={{ overflowX: "auto" }}>
              <table className="admin-table" style={{ fontSize: 12 }}>
                <thead><tr><th>SKU</th><th>Nama</th><th>HPP</th><th>Stok</th><th>Berat</th><th>Kategori</th><th>Varian</th><th>Action</th></tr></thead>
                <tbody>
                  {preview.samples.map((s, i) => (
                    <tr key={i}>
                      <td>{s.sku}</td><td>{s.title?.slice(0, 40)}</td>
                      <td>{s.cost_price?.toLocaleString("id-ID")}</td>
                      <td>{s.stock ?? 0}</td><td>{s.weight}</td>
                      <td>{s.category?.slice(0, 24)}</td>
                      <td>{(s.variant_options || []).map((v) => `${v.name}:${v.values[0]}`).join(", ")}</td>
                      <td><b>{s.action}</b>{s.issue ? ` (${s.issue})` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 12, alignItems: "center" }}>
              <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="import-mode">
                <option value="both">Tambah + Update</option>
                <option value="create">Hanya Tambah Baru</option>
                <option value="update">Hanya Update Existing</option>
              </select>
              <button className="button" onClick={commit} disabled={busy} data-testid="import-commit-btn">Konfirmasi Import</button>
            </div>
          </div>
        )}

        {batch && batch.status !== "COMPLETED" && (
          <div className="admin-block" data-testid="import-progress">
            Importing... {batch.progress || 0}% — Created: {batch.result?.created || 0} · Updated: {batch.result?.updated || 0} · Failed: {batch.result?.failed || 0}
          </div>
        )}
        {batch?.status === "COMPLETED" && (
          <div className="admin-block" data-testid="import-result">
            <h3>Import Selesai</h3>
            <p>Created: <b>{batch.result.created}</b> · Updated: <b>{batch.result.updated}</b> · Skipped: <b>{batch.result.skipped}</b> · Failed: <b>{batch.result.failed}</b></p>
            {(batch.report || []).filter((r) => r.status === "FAILED").slice(0, 20).map((r, i) => (
              <p key={i} style={{ color: "#f87171", fontSize: 12 }}>{r.sku} — {r.note}</p>
            ))}
          </div>
        )}

        <div className="admin-block" style={{ marginTop: 16 }}>
          <h3>Riwayat Upload</h3>
          <table className="admin-table" style={{ fontSize: 13 }} data-testid="import-history">
            <thead><tr><th>Tanggal</th><th>File</th><th>Admin</th><th>Total</th><th>Created</th><th>Updated</th><th>Failed</th><th>Status</th></tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td>{(h.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td>{h.filename}</td><td>{h.admin?.split("@")[0]}</td>
                  <td>{h.stats?.total}</td>
                  <td>{h.result?.created ?? "-"}</td><td>{h.result?.updated ?? "-"}</td><td>{h.result?.failed ?? "-"}</td>
                  <td>{h.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
